import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  serviceFundMapping,
  useGetDetailedOutputStatsByPeriodId,
  useGetDetailedTransformationStatsByPeriodId
} from '../../../../../services/funds.service';
import { useGetTransformationsListByPeriod } from '../../../../../services/workflow.services';

import Table from '../../../../../components/Table';
import Modal from 'react-modal';

import { IoClose } from 'react-icons/io5';

import { GoArrowUpRight } from 'react-icons/go';
import { Tooltip } from 'react-tooltip';
import {
  useAutomateSelectedTransformation,
  useCancelAutomateTransformation
} from '../../../../../services/automation.service';
import toast from 'react-hot-toast';
import { TbReload, TbSearch } from 'react-icons/tb';
import { extractApiError } from '../../../../../utils';

import {
  Container,
  TableContainerStyle,
  CloseStyle,
  Title,
  ErrorMessageStyle,
  DescriptionStyle,
  TabsContainer,
  Tabs,
  Tab,
  Badge,
  FooterStyle,
  customStyles,
  colors,
  FileNameStyle
} from './styles';
import { FiRefreshCw } from 'react-icons/fi';
import { SortButton } from '../../../components/SortButton';
import { FilterDropdown } from '../../../components/ProjectFilters/FilterDropdown';
import agentStore from '../../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../../components/ChatWidget/constants';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import { Button, Checkbox, Flex, rem, Text, TextInput } from '@mantine/core';
import { usePeriodById } from '../../../../../services/period.service';
import { getLastRefreshLabel } from '../../../../../utils';
import { DeleteConfirmModal } from '../../../../../components/DeleteConfirmModal';
import { TRANSFORMATION_TYPES } from '../../../../../consts/transformationTypes';
import { useSignalRListener } from '../../../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../../../config/signalr';

const tabs = [
  {
    label: 'Transformation Issues',
    value: 'transformation'
  },
  {
    label: 'Output Tables Issues',
    value: 'output_tables'
  }
];

const getColor = type => {
  if (type === 'Warning') return 'var(--mantine-color-gray-8)';
  else if (type === 'Mandatory fix') return 'var(--mantine-color-orange-6)';

  return 'var(--mantine-color-red-8)';
};

const getParams = element => {
  return `${element.table}_${element.category}_${element.error_type}_${element.field}`;
};

export const TransformationIssuesModal = ({
  isOpen,
  onClose,
  periodId,
  period,
  worklowId,
  refetch,
  isLatestPeriod
}) => {
  const navigate = useNavigate();

  const [selectedTransformation, setSelectedTransformation] = useState([]);
  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [hasInProgress, setHasInProgress] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categoryOptionsRef = useRef([]);

  const isScratchPeriod = period?.template_type === 'sratch';

  const apiParams = useMemo(() => {
    const params = {};
    if (debouncedSearch) params.search_query = debouncedSearch;
    if (selectedCategory) params.category_name = selectedCategory.name;
    if (sortConfig.key && !isScratchPeriod) params.sort_by = sortConfig.direction;
    return params;
  }, [isScratchPeriod, debouncedSearch, selectedCategory, sortConfig]);

  const automateSelectedTransformation = useAutomateSelectedTransformation();
  const cancelAutomateTransformation = useCancelAutomateTransformation();

  const { refetch: refetchPeriod } = usePeriodById({
    periodId,
    queryOptions: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      onSuccess: data => {
        setRefreshedAt(data?.automation_transformation_last_refreshed_time);
      }
    }
  });

  // Fetch data based on period type
  const {
    data: automationIssues,
    refetch: refetchAutomationIssues,
    isLoading: isLoadingAutomation
  } = useGetDetailedTransformationStatsByPeriodId({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      onSuccess: data => {
        const isOuterStatusInProgress = data.some(item => item.status === 'In progress');
        const isInnerStatusInProgress = data.some(item => item?.task?.status === 'In progress');

        setHasInProgress(isOuterStatusInProgress || isInnerStatusInProgress);
      }
    }
  });

  const {
    data: scratchIssues,
    refetch: refetchScratchIssues,
    isLoading: isLoadingScratch
  } = useGetTransformationsListByPeriod({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen && isScratchPeriod
    }
  });

  // Flatten nested backend response (files -> sheets -> transformations) for scratch periods
  const flatScratchIssues = useMemo(() => {
    if (!scratchIssues || !Array.isArray(scratchIssues)) return [];
    return scratchIssues.flatMap(file =>
      (file?.sheets ?? []).flatMap(sheet =>
        (sheet?.transformations ?? []).map(t => ({
          id: t.id,
          file_name: file?.name ?? '',
          file_id: file?.id,
          sheet_name: sheet?.name ?? '',
          sheet_id: sheet?.id,
          is_structured: sheet?.is_structured,
          category: t.category,
          table: t.table
        }))
      )
    );
  }, [scratchIssues]);

  const issues = isScratchPeriod ? flatScratchIssues : automationIssues;

  const visibleScratchIssues = useMemo(() => {
    if (!isScratchPeriod) return [];
    if (!sortConfig.key) return flatScratchIssues;
    const isAscending = !sortConfig.direction.startsWith('-');

    const FIELD_MAP = { category_name: 'category' };
    const sortField = FIELD_MAP[sortConfig.key] ?? sortConfig.key;
    return [...flatScratchIssues].sort((a, b) => {
      const valueA = (a[sortField] ?? '').toLowerCase();
      const valueB = (b[sortField] ?? '').toLowerCase();
      return isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  }, [isScratchPeriod, flatScratchIssues, sortConfig]);
  const refetchIssues = isScratchPeriod ? refetchScratchIssues : refetchAutomationIssues;
  const isLoading = isScratchPeriod ? isLoadingScratch : isLoadingAutomation;

  // skip OutputTable fetch entirely to avoid a redundant API call.
  const {
    data: outputIssues,
    isLoading: isLoadingOutputTables,
    refetch: refetchOutputIssues
  } = useGetDetailedOutputStatsByPeriodId({
    periodId,
    queryParams: {
      enabled: !!periodId && isOpen && !isScratchPeriod,
      // SignalR (OutputTableStatusUpdate) drives live updates while the
      // user is on the output_tables tab. The 60s heartbeat catches
      // missed messages without the previous 2s polling churn.
      refetchInterval: hasInProgress && activeTab === 'output_tables' ? 60000 : false,
      onSuccess: data => {
        const isOuterStatusInProgress = data?.some?.(item => item.status === 'In progress');
        const isInnerStatusInProgress = data?.some?.(item => item?.task?.status === 'In progress');

        setHasInProgress(isOuterStatusInProgress || isInnerStatusInProgress);
      }
    }
  });

  const onChangeTab = value => {
    setActiveTab(value);
    setHasInProgress(false);
    setSortConfig({ key: '', direction: '' });
    setSearchTerm('');
    setSelectedCategory(null);
  };

  const onCancelTransformation = useCallback(() => {
    const taskIds =
      selectedTransformation.map(
        item => issues.find(value => value.previous_transformation_id === item)?.task.id
      ) || [];

    if (taskIds.length === 0)
      return toast.error('No tasks are detected on selected transformations');

    cancelAutomateTransformation.mutate(
      {
        task_ids: selectedTransformation.map(
          item => issues.find(value => value.previous_transformation_id === item)?.task.id
        )
      },
      {
        onSuccess: () => {
          setSelectedTransformation([]);
          refetch();
          refetchIssues();
          toast.success('Automated transformations have been successfully cancelled', {
            position: 'bottom-center'
          });
        }
      }
    );
  }, [selectedTransformation, issues, refetch]);

  const onSelectTransformation = id => {
    if (selectedTransformation.includes(id)) {
      setSelectedTransformation(prev => prev.filter(value => value !== id));
    } else {
      setSelectedTransformation(prev => [...prev, id]);
    }
  };

  const isAllSelected = useMemo(() => {
    const selectableIssues = Array.isArray(issues)
      ? issues.filter(item => item?.transformation_type !== TRANSFORMATION_TYPES.MANUAL)
      : [];
    return (
      selectableIssues.length > 0 &&
      selectableIssues.every(item =>
        selectedTransformation.includes(item.previous_transformation_id)
      )
    );
  }, [selectedTransformation, issues]);

  const toggleAllTransformations = () => {
    if (isAllSelected) {
      setSelectedTransformation([]);
    } else {
      setSelectedTransformation(
        Array.isArray(issues)
          ? issues
              ?.filter(item => item?.transformation_type !== TRANSFORMATION_TYPES.MANUAL)
              ?.map(item => item.previous_transformation_id)
          : []
      );
    }
  };

  const onAutoExtract = () => {
    automateSelectedTransformation.mutate(
      {
        period_id: periodId,
        process_pairs: [...selectedTransformation].map(prevTransformationId => ({
          previous_transformation_id: prevTransformationId,
          extraction_mapping_id: issues.find(
            item => item.previous_transformation_id === prevTransformationId
          )?.extraction_mapping_id
        }))
      },
      {
        onSuccess: () => {
          setOpenConfirmModal(false);
          setSelectedTransformation([]);
          refetch();
          refetchIssues();
          toast.success('Automated transformations have been successfully applied', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          toast.error(extractApiError(err, 'Failed to apply automated transformation'), {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const [isRefreshTransformation, setIsRefreshTransformation] = useState(false);

  const refreshAutomationTransformation = useCallback(
    async ({ silent = false } = {}) => {
      setIsRefreshTransformation(true);

      try {
        await serviceFundMapping.getRefreshTransformationStats(periodId);
        await refetchIssues();
        if (!silent) {
          toast.success('Transformations have been successfully refreshed');
        }
      } catch (err) {
        refetch();
        refetchPeriod();
        if (!silent) {
          toast.error(extractApiError(err, 'Failed to refresh transformation'));
        }
      } finally {
        setIsRefreshTransformation(false);
      }
    },
    [periodId, refetch, refetchIssues, refetchPeriod]
  );

  const handleTransformationSignal = data => {
    if (
      data?.period_id === periodId &&
      isOpen &&
      !isScratchPeriod &&
      activeTab === 'transformation'
    ) {
      refetchIssues();
    }
  };

  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handleTransformationSignal
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleTransformationSignal);

  // OutputTableStatusUpdate live-drives the output_tables tab — replaces the
  // 2s polling that used to refresh detailedOutputStats. Only refetch when
  // the user is actually looking at that tab.
  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, data => {
    if (
      data?.period_id === periodId &&
      isOpen &&
      !isScratchPeriod &&
      activeTab === 'output_tables'
    ) {
      refetchOutputIssues();
    }
  });

  const onNavigate = (fileId, sheetId, category, extractionId, table) => {
    const extractParam = extractionId != null ? `&extractId=${extractionId}` : '';
    const tableParam = table ? `&extractedTransformationTable=${table}` : '';
    navigate(
      `/data-extraction/${worklowId}/${periodId}?fileId=${fileId}&sheetId=${sheetId}&category=${category}&tab=transformation${extractParam}${tableParam}`
    );
  };

  const onNavigateOutputTables = el => {
    navigate(`/output-tables/${worklowId}/${periodId}?initial_params=${getParams(el)}`);
  };

  const transformationIssuesCount = useMemo(() => {
    if (!issues) return 0;

    if (isScratchPeriod) return issues.length;

    const els = issues.filter(el => el.errors?.length > 0);

    return els.length;
  }, [issues, isScratchPeriod]);

  const mutatedOutputIssues = useMemo(() => {
    if (!outputIssues) return [];

    return [
      ...(outputIssues.errors || []),
      ...(outputIssues.mandatory_fixes || []),
      ...(outputIssues.warnings || [])
    ];
  }, [outputIssues]);

  const outputTableIssuesCount = useMemo(() => {
    if (!mutatedOutputIssues) return 0;

    return mutatedOutputIssues.length;
  }, [mutatedOutputIssues]);

  const counts = {
    transformation: transformationIssuesCount,
    output_tables: outputTableIssuesCount
  };

  const categoryOptions = useMemo(() => {
    if (!issues?.length) {
      categoryOptionsRef.current = [];
      return categoryOptionsRef.current;
    }
    if (!selectedCategory) {
      const unique = [...new Set(issues.map(item => item.category).filter(Boolean))];
      categoryOptionsRef.current = unique
        .sort()
        .map(category => ({ id: category, name: category }));
    }
    return categoryOptionsRef.current;
  }, [issues, selectedCategory]);

  const tableData = isScratchPeriod ? visibleScratchIssues : (automationIssues ?? []);

  const isMutating =
    automateSelectedTransformation.isLoading ||
    cancelAutomateTransformation.isLoading ||
    selectedTransformation.length === 0;

  const isSelectedTransformationsInProgress = useMemo(() => {
    return selectedTransformation.some(el => {
      const status = issues?.find(rec => rec.previous_transformation_id === el)?.status;
      return status === 'In progress';
    });
  }, [selectedTransformation, issues]);

  const columns = [
    {
      key: 'file_name',
      title: 'File Name',
      renderHeadCell: () => {
        return (
          <FileNameStyle>
            {isLatestPeriod && !isScratchPeriod && (
              <Checkbox checked={isAllSelected} onChange={toggleAllTransformations} />
            )}
            File Name
            <SortButton
              columnName="file_name"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </FileNameStyle>
        );
      },
      render: record => {
        const isManually = record?.transformation_type === TRANSFORMATION_TYPES.MANUAL;
        return (
          <FileNameStyle>
            {isLatestPeriod && !isScratchPeriod && (
              <Checkbox
                disabled={isManually}
                checked={selectedTransformation.includes(record.previous_transformation_id)}
                onChange={() => onSelectTransformation(record.previous_transformation_id)}
              />
            )}
            {record.file_name}
          </FileNameStyle>
        );
      },
      tdProps: {
        style: {
          fontWeight: 700
        }
      }
    },
    {
      key: 'sheet_name',
      title: 'Sheet Name',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Sheet Name
          </Text>
          <SortButton
            columnName="sheet_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'table_structure',
      title: 'Table Structure',
      render: record =>
        record.is_structured == null ? '-' : record.is_structured ? 'Structured' : 'Unstructured'
    },
    {
      key: 'category',
      title: 'Category',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Category
          </Text>
          <SortButton
            columnName="category_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'table',
      title: 'Table',
      render: record => record.table ?? '-'
    },
    ...(isScratchPeriod
      ? [
          {
            key: 'navigate',
            title: '',
            render: record => (
              <GoArrowUpRight
                onClick={() =>
                  onNavigate(
                    record.file_id,
                    record.sheet_id,
                    record.category,
                    undefined,
                    record.table
                  )
                }
                color="#228BE6"
                style={{
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              />
            )
          }
        ]
      : [
          {
            key: 'status',
            title: 'Status',
            renderHeadCell: () => (
              <Text fz="14px" lh="20px" c="#495057" fw="600">
                Status
              </Text>
            ),
            render: record => {
              const isManually = record?.transformation_type === TRANSFORMATION_TYPES.MANUAL;

              return (
                <p
                  style={{
                    color: isManually
                      ? colors['Not extracted']
                      : colors?.[record?.status || 'Not started']
                  }}
                >
                  {!isManually && record.status ? record.status : !isManually && 'Not transformed'}
                  {isManually && TRANSFORMATION_TYPES.MANUAL}
                </p>
              );
            }
          },
          {
            key: 'issue_description',
            title: 'Issue Description',
            render: record => {
              const hasErrorAndWarning = record.errors?.length > 0;

              return (
                <DescriptionStyle>
                  {(record.errors?.length > 0 || record.latest_automation_task?.error_message) && (
                    <>
                      <ErrorMessageStyle
                        style={{
                          textDecoration: record.errors?.length > 0 && 'underline',
                          color: record.errors?.length > 0 && '#FD7E14'
                        }}
                        data-tooltip-id={record.errors?.length > 0 && 'warning-tooltip'}
                        data-tooltip-content={record.errors
                          ?.map((item, index) => `${index + 1}. ${item.error_message}`)
                          .join(`\n`)}
                      >
                        {!(record?.errors?.length > 1)
                          ? record?.errors?.map((error, idx) => (
                              <span key={idx}>
                                - {error?.error_message}{' '}
                                {idx !== record?.errors?.length - 1 && <br />}
                              </span>
                            ))
                          : 'Warnings'}
                      </ErrorMessageStyle>

                      <GoArrowUpRight
                        onClick={() => onNavigate(record.file_id, record.sheet_id, record.category)}
                        color="#228BE6"
                        style={{
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                    </>
                  )}
                  {(record.status === 'Completed' || record.status === 'Failed') &&
                    !hasErrorAndWarning && (
                      <>
                        <div />
                        <GoArrowUpRight
                          onClick={() =>
                            onNavigate(
                              record.file_id,
                              record.sheet_id,
                              record.category,
                              record.extraction_id
                            )
                          }
                          color="#228BE6"
                          style={{
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                      </>
                    )}
                </DescriptionStyle>
              );
            }
          }
        ])
  ];

  const outputTableColumns = [
    {
      key: 'name',
      title: 'Warning Name',
      thStyle: {
        width: 180
      },
      render: record => {
        return (
          <DescriptionStyle
            style={{
              textDecoration: 'underline',
              color: getColor(record.error_type),
              justifyContent: 'flex-start'
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                minWidth: 8,
                minHeight: 8,
                borderRadius: 10,
                border: `1px solid ${getColor(record.error_type)}`,
                marginRight: 10
              }}
            ></span>
            <ErrorMessageStyle
              style={{
                cursor: 'pointer'
              }}
              onClick={() => onNavigateOutputTables(record)}
            >
              {record.field || 'N/A'}&nbsp;{record?.rows?.length > 0 && `(${record?.rows?.length})`}
            </ErrorMessageStyle>
          </DescriptionStyle>
        );
      }
    },
    {
      key: 'warning_description',
      title: 'Warning Description',
      render: record => {
        return (
          <DescriptionStyle>
            <ErrorMessageStyle>{record.message}</ErrorMessageStyle>

            <GoArrowUpRight
              onClick={() => onNavigateOutputTables(record)}
              color="#228BE6"
              style={{
                cursor: 'pointer',
                flexShrink: 0
              }}
            />
          </DescriptionStyle>
        );
      }
    }
  ];

  useDidUpdate(() => {
    if (isOpen) {
      setActiveTab(tabs[0].value);
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.AutomationTransformationDashboard);
    } else {
      agentStore.setCurrentSubScreen('');
      setSortConfig({ key: '', direction: '' });
      setSearchTerm('');
      setSelectedCategory(null);
    }
  }, [isOpen]);

  const getBadgeColorForTransformation = () => {
    if (issues?.some(issue => (issue?.task?.status || issue?.status) === 'Failed'))
      return 'var(--mantine-color-red-8)';
    else if (issues?.some(issue => (issue?.task?.status || issue?.status) === 'In progress'))
      return 'var(--mantine-color-orange-6)';

    return '#868e96';
  };

  const getBadgeColorForOutputTables = () => {
    if (mutatedOutputIssues?.some(issue => issue?.error_type === 'Error'))
      return 'var(--mantine-color-red-8)';
    else if (mutatedOutputIssues?.some(issue => issue?.error_type === 'Mandatory fix'))
      return 'var(--mantine-color-orange-6)';

    return '#868e96';
  };

  const getBadgeColor = value => {
    if (value === 'transformation') return getBadgeColorForTransformation();

    return getBadgeColorForOutputTables();
  };

  return (
    <>
      <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
        <Container>
          <CloseStyle onClick={onClose} data-testid="close-modal">
            <IoClose fontSize="18px" color="#868E96" />
          </CloseStyle>
          <Title>{isScratchPeriod ? 'Transformation List' : 'Issues'}</Title>

          <TabsContainer>
            <Tabs>
              {tabs.map(el => (
                <Tab
                  key={el.value}
                  onClick={() => onChangeTab(el.value)}
                  className={el.value === activeTab && 'active'}
                >
                  <span>{el.label}</span>{' '}
                  <Badge color={getBadgeColor(el.value)}>{counts[el.value]}</Badge>
                </Tab>
              ))}
            </Tabs>

            {activeTab === 'transformation' && !isScratchPeriod && (
              <Flex gap={rem(16)} align="center">
                {refreshedAt && (
                  <Text fz={rem(14)} fw={400} c="var(--dx-c-gray-6)">
                    {getLastRefreshLabel(refreshedAt)}
                  </Text>
                )}

                <Button
                  leftSection={<FiRefreshCw />}
                  size="xs"
                  onClick={refreshAutomationTransformation}
                  disabled={isRefreshTransformation || issues?.length === 0}
                >
                  Refresh
                </Button>
              </Flex>
            )}
          </TabsContainer>

          {(isScratchPeriod || activeTab === 'transformation') && (
            <Flex gap={16} align="center" mb={16}>
              <TextInput
                placeholder="Search"
                leftSection={<TbSearch size={16} color="#ADB5BD" />}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                size="sm"
                w={256}
              />
              <FilterDropdown
                label="Category"
                placeholder="All Categories"
                options={categoryOptions}
                value={selectedCategory}
                onChange={setSelectedCategory}
              />
            </Flex>
          )}

          <TableContainerStyle>
            {activeTab === 'transformation' ? (
              <Table columns={columns} hasIndex={false} data={tableData} isLoading={isLoading} />
            ) : (
              <Table
                columns={outputTableColumns}
                hasIndex={false}
                data={mutatedOutputIssues}
                isLoading={isLoadingOutputTables}
              />
            )}
          </TableContainerStyle>

          {isLatestPeriod && !isScratchPeriod && activeTab === 'transformation' && (
            <FooterStyle>
              <Button
                size="sm"
                leftSection={<TbReload fontSize="16px" />}
                onClick={() => setOpenConfirmModal(true)}
                disabled={isMutating}
              >
                Re-apply Transformation
              </Button>

              {selectedTransformation.length > 0 && isSelectedTransformationsInProgress && (
                <Button
                  size="sm"
                  leftSection={<IoClose fontSize="16px" />}
                  disabled={isMutating}
                  onClick={onCancelTransformation}
                  color="red.5"
                  variant="outline"
                >
                  Cancel Transformation
                </Button>
              )}
            </FooterStyle>
          )}

          <Tooltip
            id="warning-tooltip"
            style={{
              background: '#868E96',
              padding: '8px'
            }}
          />
        </Container>
      </Modal>

      <DeleteConfirmModal
        isOpen={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title="Re-apply Transformation"
        description="The transformation actions from previous period will be applied for the selected items and overwrites the existing actions. Do you want to re-apply the transformations from previous period?"
        cancelLabel="No"
        submitLabel="Yes"
        onConfirm={onAutoExtract}
        submitColor="blue.6"
        isLoading={automateSelectedTransformation.isLoading}
        // Parent TransformationIssuesModal is a react-modal at zIndex 1000/1001.
        // Mantine's default Modal zIndex (200) would render this confirm
        // visually behind it; bump above the parent so the user can see it.
        zIndex={2000}
      />
    </>
  );
};
